import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { onboardingChecklistApi, notificationRulesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, Circle, Send, X, Loader2, Rocket, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TestPushButton } from '@/components/TestPushButton';

const ITEM_TARGETS = {
  organisation: { path: '/schools', access: 'super' },
  school: { path: '/schools', access: 'super' },
  users: { path: '/users', access: 'admin' },
  sensors_registered: { path: '/devices', access: 'all' },
  sensors_connected: { path: '/devices', access: 'all' },
  recipients: { path: '/notification-rules', access: 'super' },
  notifications_tested: { dialog: true, access: 'admin' },
};

export function OnboardingChecklist() {
  const navigate = useNavigate();
  const { isSuperAdmin, canCreate } = useAuth();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await onboardingChecklistApi.get();
      setData(res.data);
    } catch { /* silent — checklist is non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSendTest = async () => {
    if (!email.trim()) { toast.error('Ange en e-postadress'); return; }
    setSending(true);
    try {
      const res = await notificationRulesApi.sendTest({ channel: 'email', target_email: email.trim(), school_id: data?.school_id });
      if (res.data.success) {
        toast.success('Testnotis skickad');
        setTestOpen(false);
        setEmail('');
        load();
      } else {
        toast.error(res.data.error || 'Testnotisen kunde inte skickas');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka testnotis');
    } finally { setSending(false); }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await onboardingChecklistApi.dismiss(data?.school_id);
      setHidden(true);
    } catch {
      toast.error('Kunde inte dölja checklistan');
    } finally { setDismissing(false); }
  };

  const canOpen = (access) => access === 'all' || (access === 'admin' ? canCreate() : isSuperAdmin());
  const openItem = (item) => {
    const t = ITEM_TARGETS[item.key];
    if (!t || !canOpen(t.access)) return;
    if (t.dialog) setTestOpen(true); else navigate(t.path);
  };

  if (!data || !data.available || data.dismissed || hidden) return null;

  const { items, completed_count, total, percent, all_done } = data;

  return (
    <Card className={cn("border", all_done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white")} data-testid="onboarding-checklist">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            {all_done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Rocket className="h-5 w-5 text-navy" />}
            <h3 className="font-chivo font-bold text-lg text-navy">
              {all_done ? 'Vapelarm är redo' : 'Kom igång med Vapelarm'}
            </h3>
          </div>
          {all_done && (
            <Button variant="ghost" size="sm" onClick={handleDismiss} disabled={dismissing} className="font-ibm gap-1 text-slate-500" data-testid="dismiss-onboarding">
              {dismissing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Dölj
            </Button>
          )}
        </div>

        {all_done ? (
          <p className="text-sm text-emerald-700 font-ibm mt-1">
            Alla grundläggande inställningar är klara och systemet är redo att användas.
          </p>
        ) : (
          <div className="mt-3 mb-4">
            <div className="flex items-center justify-between text-xs font-ibm text-slate-500 mb-1">
              <span>{completed_count} av {total} klara</span>
              <span className="tabular-nums font-semibold">{percent} %</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
          {items.map(item => {
            const t = ITEM_TARGETS[item.key];
            const clickable = t && canOpen(t.access);
            return (
              <button
                type="button"
                key={item.key}
                onClick={() => openItem(item)}
                disabled={!clickable}
                className={cn(
                  "group flex items-center justify-between gap-2 py-1.5 px-2 -mx-2 rounded-md text-left transition-colors",
                  clickable ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"
                )}
                data-testid={`checklist-item-${item.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {item.done
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                  <span className={cn("text-sm font-ibm truncate", item.done ? "text-slate-700" : "text-slate-500", clickable && "group-hover:text-navy group-hover:underline underline-offset-2")}>
                    {item.label}
                  </span>
                </div>
                {!clickable && !item.done && (
                  <span className="text-[11px] font-ibm text-slate-400 shrink-0">Hanteras av Vapelarm</span>
                )}
                {clickable && (
                  !item.done
                    ? <span className="text-xs font-ibm font-semibold text-navy flex items-center gap-0.5 shrink-0">{t.dialog ? <><Send className="h-3 w-3 mr-0.5" />Testa</> : 'Åtgärda'}<ChevronRight className="h-3.5 w-3.5" /></span>
                    : <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-navy shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </CardContent>

      {/* Test notification dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="sm:max-w-md" data-testid="test-notification-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Skicka testnotis</DialogTitle>
            <DialogDescription className="font-ibm">
              Verifiera att notifieringen kommer fram. Testet märks "TEST – Vapelarm".
              Detta påverkar inte statistik eller händelser.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
            <p className="text-sm font-ibm font-semibold text-navy">Pushnotis till din telefon</p>
            <TestPushButton size="sm" className="w-full" testId="test-push-btn-checklist" onSuccess={() => { setTestOpen(false); load(); }} />
          </div>
          <p className="text-center text-xs font-ibm text-slate-400">eller via e-post</p>
          <div className="space-y-2 pb-2">
            <Label className="font-ibm text-xs uppercase tracking-widest">Mottagarens e-post</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="rektor@skola.se" className="bg-slate-50" data-testid="test-notification-email" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} className="font-ibm">Avbryt</Button>
            <Button onClick={handleSendTest} disabled={sending} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="send-test-notification">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Skicka testnotis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
